import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import { ToolLayout } from '../ToolLayout';
import { CodeEditor } from '../CodeEditor';

const SAMPLES: Record<Mode, string> = {
  encode: 'Hello, World! This is a Base64 encoding test.\n\n{"user": "backstage", "token": "abc123"}',
  decode: 'SGVsbG8sIFdvcmxkISBUaGlzIGlzIGEgQmFzZTY0IGVuY29kaW5nIHRlc3QuCgp7InVzZXIiOiAiYmFja3N0YWdlIiwgInRva2VuIjogImFiYzEyMyJ9',
};

const useStyles = makeStyles()((theme) => ({
  layout: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2), height: 'calc(100vh - 260px)', minHeight: 400 },
  controls: { display: 'flex', alignItems: 'center', gap: theme.spacing(2) },
  row: { flex: 1, display: 'flex', gap: theme.spacing(2), alignItems: 'stretch', overflow: 'hidden' },
  pane: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  paneHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    minHeight: 36,
  },
  paneLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  midCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: theme.spacing(1), paddingTop: theme.spacing(3.5), flexShrink: 0 },
}));

type Mode = 'encode' | 'decode';

function doEncode(input: string): { result: string; error: null } | { result: null; error: string } {
  try {
    return { result: btoa(unescape(encodeURIComponent(input))), error: null };
  } catch {
    return { result: null, error: 'Failed to encode — input may contain invalid characters.' };
  }
}

function doDecode(input: string): { result: string; error: null } | { result: null; error: string } {
  try {
    return { result: decodeURIComponent(escape(atob(input.trim()))), error: null };
  } catch {
    return { result: null, error: 'Invalid Base64 string — could not decode.' };
  }
}

export function Base64Page() {
  const { classes } = useStyles();
  const [mode, setMode] = useState<Mode>('encode');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const process = (value: string, currentMode: Mode) => {
    if (!value.trim()) { setOutput(''); setError(null); return; }
    const { result, error: err } = currentMode === 'encode' ? doEncode(value) : doDecode(value);
    if (err) { setError(err); setOutput(''); }
    else { setOutput(result!); setError(null); }
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    process(value, mode);
  };

  const handleModeChange = (_: unknown, val: Mode | null) => {
    if (!val) return;
    const newInput = output || input;
    setMode(val);
    setInput(newInput);
    process(newInput, val);
  };

  const clear = () => { setInput(''); setOutput(''); setError(null); setCopied(false); };

  const loadSample = () => {
    const sample = SAMPLES[mode];
    setInput(sample);
    process(sample, mode);
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const ext = mode === 'encode' ? 'txt' : 'txt';
    const blob = new Blob([output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `base64-output.${ext}`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ToolLayout
      title="Base64 Encode / Decode"
      subtitle="Encode strings to Base64 or decode Base64 back to text"
    >
      <Box className={classes.layout}>
        <Box className={classes.controls}>
          <ToggleButtonGroup
            size="small"
            value={mode}
            exclusive
            onChange={handleModeChange}
          >
            <ToggleButton value="encode" sx={{ textTransform: 'none', fontSize: 13 }}>Encode</ToggleButton>
            <ToggleButton value="decode" sx={{ textTransform: 'none', fontSize: 13 }}>Decode</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box className={classes.row}>
          <Box className={classes.pane}>
            <Box className={classes.paneHeader}>
              <Typography className={classes.paneLabel}>
                {mode === 'encode' ? 'Plain Text' : 'Base64 Input'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Button size="small" variant="outlined" onClick={loadSample} sx={{ textTransform: 'none', fontSize: 12 }}>
                  Load sample
                </Button>
                <Tooltip title="Clear">
                  <span>
                    <IconButton size="small" onClick={clear} disabled={!input && !output}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <CodeEditor
              value={input}
              onChange={handleInputChange}
              sx={{ flex: 1 }}
              placeholder={mode === 'encode' ? 'Enter text to encode…' : 'Paste Base64 to decode…'}
            />
          </Box>

          <Box className={classes.midCol}>
            <Typography variant="caption" color="textSecondary">
              {mode === 'encode' ? '→' : '←'}
            </Typography>
          </Box>

          <Box className={classes.pane}>
            <Box className={classes.paneHeader}>
              <Typography className={classes.paneLabel}>
                {mode === 'encode' ? 'Base64 Output' : 'Plain Text'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy output'}>
                  <span>
                    <IconButton size="small" onClick={copyOutput} disabled={!output}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Download">
                  <span>
                    <IconButton size="small" onClick={download} disabled={!output}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <CodeEditor
              value={output}
              readOnly
              sx={{ flex: 1 }}
              placeholder="Output will appear here…"
            />
          </Box>
        </Box>
      </Box>
    </ToolLayout>
  );
}
